import { useProjectStore } from "@/stores/project-store";
import { RefreshCw, ExternalLink, Plus, X, ArrowLeft, ArrowRight, Globe, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { wsClient } from "@/lib/ws-client";

interface IframePanelProps {
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

interface BrowserTab {
  id: string;
  title: string;
  url: string;
  color: string;
  iframeKey: number;
}

const TAB_COLORS = ["bg-blue-400", "bg-orange-400", "bg-green-400", "bg-purple-400", "bg-pink-400", "bg-yellow-400", "bg-cyan-400", "bg-red-400"];

let tabIdCounter = 0;
function nextTabId() {
  return `tab-${++tabIdCounter}`;
}

export function IframePanel({ isExpanded, onToggleExpand }: IframePanelProps) {
  const projectId = useProjectStore((s) => s.projectId);
  const proxyIframeUrl = useProjectStore((s) => s.proxyIframeUrl);
  const activeWorkspaceId = useProjectStore((s) => s.activeWorkspaceId);

  const iframeBaseUrl = proxyIframeUrl ?? "";

  // Store refs to all iframe elements
  const iframeRefs = useRef<Map<string, HTMLIFrameElement>>(new Map());

  const [tabs, setTabs] = useState<BrowserTab[]>(() => [
    {
      id: nextTabId(),
      title: "n8n workspace",
      url: `${iframeBaseUrl}/`,
      color: TAB_COLORS[0],
      iframeKey: 0,
    },
  ]);
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? "");

  useEffect(() => {
    return wsClient.onMessage((msg) => {
      if (msg.type !== "mcp_cmd_ready") return;
      const payload = { type: "mcp_cmd_ready", projectId: msg.projectId, callId: msg.callId, tool: msg.tool, args: msg.args };
      iframeRefs.current.forEach((iframe) => {
        iframe.contentWindow?.postMessage(payload, "*");
      });
    });
  }, []);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const { type } = event.data || {};
      if (!type) return;

      if (type === "mcp_result_ready") {
        const id = event.data.projectId || activeWorkspaceId;
        wsClient.send({ type: "mcp_result_ready", projectId: id, callId: event.data.callId });
      } else if (type.startsWith("n8n_")) {
        wsClient.send(event.data);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [activeWorkspaceId]);

  const activeTab = tabs.find((t) => t.id === activeTabId);

  const addTab = useCallback(() => {
    const newTab: BrowserTab = {
      id: nextTabId(),
      title: "New Tab",
      url: `${iframeBaseUrl}/`,
      color: TAB_COLORS[tabs.length % TAB_COLORS.length],
      iframeKey: 0,
    };
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newTab.id);
  }, [tabs.length, iframeBaseUrl]);

  const closeTab = useCallback(
    (tabId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setTabs((prev) => {
        if (prev.length <= 1) return prev;
        const next = prev.filter((t) => t.id !== tabId);
        if (activeTabId === tabId) {
          const closedIdx = prev.findIndex((t) => t.id === tabId);
          setActiveTabId(next[Math.min(closedIdx, next.length - 1)].id);
        }
        return next;
      });
    },
    [activeTabId],
  );

  const handleRefresh = useCallback(() => {
    setTabs((prev) => prev.map((t) => (t.id === activeTabId ? { ...t, iframeKey: t.iframeKey + 1 } : t)));
  }, [activeTabId]);

  const handleOpenExternal = useCallback(() => {
    if (activeTab) window.open(activeTab.url, "_blank");
  }, [activeTab]);

  if (!projectId) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">No active project</div>;
  }

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      {/* Tab Bar */}
      <div className="flex items-end bg-muted/50 pt-2 px-2 gap-0.5">
        <div className="flex items-end gap-0.5 min-w-0 overflow-x-auto scrollbar-none flex-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTabId(tab.id)}
              className={cn("group/tab flex items-center gap-2 min-w-0 max-w-[180px] rounded-t-lg px-3 py-1.5 text-xs transition-colors", tab.id === activeTabId ? "bg-background text-foreground" : "bg-transparent text-muted-foreground hover:bg-muted")}
            >
              <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", tab.color)} />
              <span className="truncate">{tab.title}</span>
              {tabs.length > 1 && (
                <span onClick={(e) => closeTab(tab.id, e)} className="ml-auto shrink-0 rounded p-0.5 opacity-0 group-hover/tab:opacity-100 hover:bg-muted-foreground/20 transition-opacity">
                  <X className="h-3 w-3" />
                </span>
              )}
            </button>
          ))}
        </div>
        <button onClick={addTab} className="mb-1 ml-1 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="New tab">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Navigation Bar */}
      <div className="flex items-center gap-1.5 border-b border-border bg-background px-2 py-1.5">
        <Button variant="ghost" size="icon-xs" title="Back" disabled>
          <ArrowLeft className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon-xs" title="Forward" disabled>
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon-xs" onClick={handleRefresh} title="Refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>

        {/* URL Bar */}
        <div className="flex flex-1 items-center gap-2 rounded-full bg-muted/60 border border-border px-3 py-1 min-w-0">
          <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="text-xs text-muted-foreground truncate">{activeTab?.url}</span>
        </div>

        <Button variant="ghost" size="icon-xs" onClick={handleOpenExternal} title="Open in new tab">
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>

        {onToggleExpand && (
          <Button variant="ghost" size="icon-xs" onClick={onToggleExpand} title={isExpanded ? "Restore split view" : "Expand browser"}>
            {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {/* Iframe Content - render all tabs, show active */}
      <div className="flex-1 min-h-0 relative">
        {tabs.map((tab) => (
          <iframe
            key={`${tab.id}-${tab.iframeKey}`}
            ref={(el) => {
              if (el) {
                iframeRefs.current.set(tab.id, el);
              } else {
                iframeRefs.current.delete(tab.id);
              }
            }}
            src={tab.url}
            sandbox="allow-storage-access-by-user-activation allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
            className={cn("absolute inset-0 h-full w-full border-none", tab.id === activeTabId ? "z-10" : "z-0 invisible")}
            data-workspace-iframe={tab.id === activeTabId ? "active" : undefined}
            title={tab.title}
          />
        ))}
      </div>
    </div>
  );
}
